// ----------------------------------------------------------------------- imports externos
import { useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import { useAuth } from "../auth/AuthContext";
import Loading from "../routes/Loading";
import CuentaDeBaja from "../routes/CuentaDeBaja";
import useDelay from "./systemFunctions";

export const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading, mustResetPassword, deBaja } = useAuth();
  const showLoading = useDelay(loading, 1000);
  const location = useLocation();

  /* A DÓNDE IBA viaja con la redirección: el QR de una llave (ticket 522)
   * abre `/llaves/1004`, y si el celular no tenía la sesión abierta, después
   * de entrar tiene que caer en esa llave y no en el inicio. */
  if (!isAuthenticated && !loading) {
    const volverA = `${location.pathname}${location.search || ""}`;
    return (
      <Navigate
        to="/login"
        replace
        state={volverA && volverA !== "/" ? { volverA } : undefined}
      />
    );
  }

  /* Una cuenta dada de baja no ve el hub: ni el menú ni ninguna pantalla,
   * tampoco las que no piden permiso. Sólo el aviso y el botón de salir.
   * Va antes que el cambio de clave: a quien no puede entrar no se le pide
   * que elija una contraseña. */
  if (isAuthenticated && !loading && deBaja) {
    return <CuentaDeBaja />;
  }

  if (
    isAuthenticated &&
    !loading &&
    mustResetPassword &&
    location.pathname !== "/cambiar-password"
  ) {
    return <Navigate to="/cambiar-password" replace />;
  }

  return (
    <>
      {children}
      {showLoading && <Loading />}
    </>
  );
};

/* Sólo saca a quien YA ESTABA logueado al llegar, no a quien se loguea acá.
 *
 * Antes sacaba a los dos, y eso se comió la pantalla de carga. En cuanto la
 * contraseña se aceptaba, `isAuthenticated` pasaba a true, este componente
 * devolvía el <Navigate> en ese mismo render y el Login se desmontaba con la
 * animación sin haber empezado. No se rompía nada visible: simplemente no
 * aparecía, que es la forma más difícil de darse cuenta.
 *
 * Antes no pasaba porque la transición corría después del CORREO, cuando
 * todavía no había sesión. Al moverla después de la contraseña —que es donde
 * de verdad hay que esperar— quedó justo donde este guard la mataba.
 *
 * Los dos casos siguen resueltos y son distintos: el que abre /login con la
 * sesión puesta no tiene nada que hacer ahí y se va derecho; el que se acaba
 * de loguear se queda hasta que la pantalla de carga termine y navegue sola.
 */
export const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  // Se decide UNA vez, cuando se sabe: mientras `loading` está prendido la
  // sesión todavía se está restaurando y `isAuthenticated` no significa nada.
  const llegoLogueada = useRef(null);
  if (llegoLogueada.current === null && !loading) {
    llegoLogueada.current = isAuthenticated;
  }

  if (isAuthenticated && !loading && llegoLogueada.current) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      {children}
      {loading && <Loading />}
    </>
  );
};

/* A dónde va el que acaba de entrar: a la dirección que traía `PrivateRoute`
 * si es una del hub (empieza con una sola «/», no es el login), y si no, al
 * inicio. Nunca a otro dominio: `//otro.com` y `https://…` no son del hub. */
export const destinoDespuesDelLogin = (state) => {
  const v = state?.volverA;
  if (typeof v !== "string" || !v.startsWith("/") || v.startsWith("//")) return "/";
  if (v === "/login" || v.startsWith("/login?") || v.startsWith("/login/")) return "/";
  return v;
};
